import Vue from 'vue';
import types from '../../mutation-types';
import ContactConversationApi from '../../../api/inbox/contactConversation';
import MessageApi from '../../../api/inbox/message';
import { MESSAGE_STATUS, MESSAGE_TYPE } from 'shared/constants/messages';
import { createPendingMessage } from 'dashboard/helper/commons';
// createPendingMessage: This function is used to create a pending message object with the necessary attributes before sending it.
// It takes the message data as input and returns a message object with a status of 'pending'.
import {
  buildContactsViewsConversationList,
  isOnMentionsView,
  isOnUnattendedView,
  isOnFoldersView,
} from './helpers/actionHelpers';
import messageReadActions from './actions/messageReadActions';
import messageTranslateActions from './actions/messageTranslateActions';

const MAC_LIMIT_EXTERNAL_ERROR =
  'Mensaje no enviado: se supero el limite de MAC del plan contratado.';

const isMacLimitError = ({ errorMessage = '', errorCode = '', status = 0 }) => {
  const normalized = String(errorMessage).toLowerCase();
  return (
    status === 402 ||
    errorCode === 'MAC_LIMIT_REACHED' ||
    normalized.includes('contactos activos mensuales') ||
    normalized.includes('mac')
  );
};

export const hasMessageFailedWithExternalError = pendingMessage => {
  // This helper is used to check if the message has failed with an external error.
  // We have two cases
  // 1. Messages that fail from the UI itself (due to large attachments or a failed network):
  //    In this case, the message will have a status of failed but no external error. So we need to create that message again
  // 2. Messages sent from Chatwoot but failed to deliver to the customer for some reason (user blocking or client system down):
  //    In this case, the message will have a status of failed and an external error. So we need to retry that message
  const { content_attributes: contentAttributes, status } = pendingMessage;
  const externalError = contentAttributes?.external_error ?? '';
  return status === MESSAGE_STATUS.FAILED && externalError !== '';
};

// actions
const actions = {
  getContactViewConversation: async ({ commit }, conversationId) => {
    try {
     
      const response = await ContactConversationApi.show(conversationId);
      commit(types.CONTACT_UPDATE_CONVERSATION, response.data);
      commit(`contacts/${types.SET_CONTACT_ITEM}`, response.data.meta.sender);
    } catch (error) {
      // Ignore error
    }
  },

  getContactViewContact: async ({ commit, state }, contactId) => {
    try {
      const response = await ContactConversationApi.showByContact(contactId);
      const conversation = response.data;

      // Check if conversation already exists in the list
      const exists = state.allContactViewConversations.some(
        c => c.id === conversation.id
      );
      if (exists) {
        commit(types.CONTACT_UPDATE_CONVERSATION, conversation);
      } else {
        // Add to the list so setActiveChat can find it
        commit(types.CONTACT_ADD_CONVERSATION, conversation);
      }
      commit(`contacts/${types.SET_CONTACT_ITEM}`, conversation.meta.sender);
    } catch (error) {
      // Ignore error
    }
  },

  fetchContactViewAllConversations: async ({ commit, state, dispatch }) => {
    commit(types.CONTACT_SET_LIST_LOADING_STATUS);
    try {
      // Same as conversation section: use store filters (updated by component before dispatch)
      const params = state.conversationFilters;
      const response = await ContactConversationApi.getGroupedByContact(params);
      // 304 or empty body: avoid using stale/missing data and always clear loading
      const data = response?.data?.data;
      const conversationList = data?.payload ?? [];
      const metaData = data?.meta ?? {};
      buildContactsViewsConversationList(
        { commit, dispatch },
        params,
        { payload: conversationList, meta: metaData },
        params.assigneeType
      );
    } catch (error) {
      commit(types.CONTACT_CLEAR_LIST_LOADING_STATUS);
    }
  },

  fetchContactViewFilteredConversations: async ({ commit, dispatch }, params) => {
   
    commit(types.CONTACT_SET_LIST_LOADING_STATUS);
    try {
      const {
        data: { data },
      } = await ContactConversationApi.filter_grouped(params);



buildContactsViewsConversationList(
        { commit, dispatch },
        params,
        data,
        'appliedFilters'
      );
    } catch (error) {
      // Handle error
    }
  },

  emptyContactViewAllConversations({ commit }) {
    commit(types.CONTACT_EMPTY_ALL_CONVERSATION);
  },

  clearContactViewSelectedState({ commit }) {
    commit(types.CONTACT_CLEAR_CURRENT_CHAT_WINDOW);
  },

  fetchContactViewPreviousMessages: async ({ commit }, data) => {
    try {
      const {
        data: { meta, payload },
      } = await MessageApi.getPreviousMessagesByContactId(data);

      commit(`conversationMetadata/${types.SET_CONVERSATION_METADATA}`, {
        id: data.conversationId,
        data: meta,
      });
      commit(types.CONTACT_SET_PREVIOUS_CONVERSATIONS, {
        id: data.conversationId,
        data: payload,
        contactId: data.contactId,
      });
      if (!payload.length) {
        commit(types.CONTACT_SET_ALL_MESSAGES_LOADED);
      }
    } catch (error) {
      // Handle error
      console.error('Error en fetchContactViewPreviousMessages:', error);
    }
  },

  fetchContactViewAllAttachments: async ({ commit }, conversationId) => {
    try {
      const { data } = await ContactConversationApi.getAllAttachments(conversationId);
      commit(types.CONTACT_SET_ALL_ATTACHMENTS, {
        id: conversationId,
        data: data.payload,
      });
    } catch (error) {
      // Handle error
    }
  },

  syncContactViewActiveConversationMessages: async (
    { commit, state, dispatch },
    { conversationId }
  ) => {
    const { allContactViewConversations, syncConversationsMessages } = state;
    const lastMessageId = syncConversationsMessages[conversationId];
    const selectedChat = allContactViewConversations.find(
      conversation => conversation.id === conversationId
    );
    if (!selectedChat) return;
    try {
      const { messages } = selectedChat;
      // Fetch all the messages after the last message id
      const {
        data: { meta, payload },
      } = await MessageApi.getPreviousMessages({
        conversationId,
        after: lastMessageId,
      });
      commit(`conversationMetadata/${types.SET_CONVERSATION_METADATA}`, {
        id: conversationId,
        data: meta,
      });
      // Find the messages that are not already present in the store
      const missingMessages = payload.filter(
        message => !messages.find(item => item.id === message.id)
      );
      selectedChat.messages.push(...missingMessages);
      // Sort the messages by created_at
      const sortedMessages = selectedChat.messages.sort((a, b) => {
        return new Date(a.created_at) - new Date(b.created_at);
      });
      commit(types.CONTACT_SET_MISSING_MESSAGES, {
        id: conversationId,
        data: sortedMessages,
      });
      commit(types.CONTACT_SET_LAST_MESSAGE_ID_IN_SYNC_CONVERSATION, {
        conversationId,
        messageId: null,
      });
      // Do not call markMessagesRead here; MessagesView.makeMessagesRead() handles it once.
    } catch (error) {
      // Handle error
    }
  },

  setContactViewConversationLastMessageId: async (
    { commit, state },
    { conversationId }
  ) => {
    const { allContactViewConversations } = state;
    const selectedChat = allContactViewConversations.find(
      conversation => conversation.id === conversationId
    );
    if (!selectedChat) return;
    const { messages } = selectedChat;
    const lastMessage = messages.last();
    if (!lastMessage) return;
    commit(types.CONTACT_SET_LAST_MESSAGE_ID_IN_SYNC_CONVERSATION, {
      conversationId,
      messageId: lastMessage.id,
    });
  },

  async setContactViewActiveChat({ commit, dispatch }, { data, after, contactId }) {
    commit(types.CONTACT_SET_CURRENT_CHAT_WINDOW, data);
    commit(types.CONTACT_CLEAR_ALL_MESSAGES_LOADED);
    if (data.dataFetched === undefined) {
      try {
        await dispatch('fetchContactViewPreviousMessages', {
          after,
          before: data.messages[0].id,
          conversationId: data.id,
          contactId,
        });
        Vue.set(data, 'dataFetched', true);
      } catch (error) {
        // Ignore error
      }
    }
  },

  assignContactViewAgent: async (
    { dispatch },
    { conversationId, agentId, previousAssigneeId = null }
  ) => {
    try {
      const response = await ContactConversationApi.assignAgent({
        conversationId,
        agentId,
      });
      dispatch('setContactViewCurrentChatAssignee', response.data);

      // Update tab counts based on assignee change
      const newAssigneeId = response.data?.id || null;
      dispatch(
        'contactViewConversationStats/updateOnAssigneeChange',
        { previousAssigneeId, newAssigneeId },
        { root: true }
      );
    } catch (error) {
      // Handle error
    }
  },

  setContactViewCurrentChatAssignee({ commit }, assignee) {
    commit(types.CONTACT_ASSIGN_AGENT, assignee);
  },

  assignContactViewTeam: async ({ dispatch }, { conversationId, teamId }) => {
    try {
      const response = await ContactConversationApi.assignTeam({
        conversationId,
        teamId,
      });
      dispatch('setContactViewCurrentChatTeam', { team: response.data, conversationId });
    } catch (error) {
      // Handle error
    }
  },

  setContactViewCurrentChatTeam({ commit }, { team, conversationId }) {
    commit(types.CONTACT_ASSIGN_TEAM, { team, conversationId });
  },

  toggleContactViewStatus: async (
    { commit },
    { conversationId, status, snoozedUntil = null }
  ) => {
    try {
      const {
        data: {
          payload: {
            current_status: updatedStatus,
            snoozed_until: updatedSnoozedUntil,
          } = {},
        } = {},
      } = await ContactConversationApi.toggleStatus({
        conversationId,
        status,
        snoozedUntil,
      });
      commit(types.CONTACT_CHANGE_CONVERSATION_STATUS, {
        conversationId,
        status: updatedStatus,
        snoozedUntil: updatedSnoozedUntil,
      });
    } catch (error) {
      // Handle error
    }
  },

  resolveContactViewWithClosingNote: async (
    { commit },
    { conversationId, convStatusTag, convSummary }
  ) => {
    const {
      data: {
        payload: {
          current_status: updatedStatus,
          snoozed_until: updatedSnoozedUntil,
          custom_attributes: customAttributes,
        } = {},
      } = {},
    } = await ContactConversationApi.resolveWithClosingNote({
      conversationId,
      convStatusTag,
      convSummary,
    });
    commit(types.CONTACT_CHANGE_CONVERSATION_STATUS, {
      conversationId,
      status: updatedStatus,
      snoozedUntil: updatedSnoozedUntil,
    });
    if (customAttributes) {
      commit(types.CONTACT_UPDATE_CONVERSATION_CUSTOM_ATTRIBUTES, {
        conversationId,
        customAttributes,
      });
    }
  },

  createContactViewPendingMessageAndSend: async ({ dispatch }, data) => {

    console.log(data);

    const pendingMessage = createPendingMessage(data);

    console.log("pendingMessage");
    console.log(pendingMessage);

    dispatch('sendContactViewMessageWithData', pendingMessage);
  },

  sendContactViewMessageWithData: async ({ commit }, pendingMessage) => {
    const { conversation_id: conversationId, id } = pendingMessage;
    try {
      commit(types.CONTACT_ADD_MESSAGE, {
        ...pendingMessage,
        status: MESSAGE_STATUS.PROGRESS,
      });
      const response = hasMessageFailedWithExternalError(pendingMessage)
        ? await MessageApi.retry(conversationId, id)
        : await MessageApi.create(pendingMessage);
      commit(types.CONTACT_ADD_MESSAGE, {
        ...response.data,
        status: MESSAGE_STATUS.SENT,
      });
      commit(types.CONTACT_ADD_CONVERSATION_ATTACHMENTS, {
        ...response.data,
        status: MESSAGE_STATUS.SENT,
      });
    } catch (error) {
      const errorMessage = error.response ? error.response.data.error : undefined;
      const errorCode = error.response?.data?.error_code;
      const status = error.response?.status;
      const externalError = isMacLimitError({
        errorMessage,
        errorCode,
        status,
      })
        ? MAC_LIMIT_EXTERNAL_ERROR
        : errorMessage;

      commit(types.CONTACT_ADD_MESSAGE, {
        ...pendingMessage,
        content_attributes: {
          ...(pendingMessage.content_attributes || {}),
          external_error: externalError,
        },
        meta: {
          error: errorMessage,
        },
        status: MESSAGE_STATUS.FAILED,
      });
      throw error;
    }
  },

  addContactViewMessage({ commit }, message) {
    commit(types.CONTACT_ADD_MESSAGE, message);
    if (message.message_type === MESSAGE_TYPE.INCOMING) {
      commit(types.CONTACT_SET_CONVERSATION_CAN_REPLY, {
        conversationId: message.conversation_id,
        canReply: true,
      });
      commit(types.CONTACT_ADD_CONVERSATION_ATTACHMENTS, message);
    }
  },

  updateContactViewMessage({ commit }, message) {
    commit(types.CONTACT_ADD_MESSAGE, message);
  },

  deleteContactViewMessage: async function deleteLabels(
    { commit },
    { conversationId, messageId }
  ) {
    try {
      const { data } = await MessageApi.delete(conversationId, messageId);
      commit(types.CONTACT_ADD_MESSAGE, data);
      commit(types.CONTACT_DELETE_CONVERSATION_ATTACHMENTS, data);
    } catch (error) {
      throw new Error(error);
    }
  },

  addContactViewConversation({ commit, state, dispatch, rootState }, conversation) {
    const { currentInbox, appliedFilters } = state;
    const {
      inbox_id: inboxId,
      meta: { sender },
    } = conversation;
    const hasAppliedFilters = !!appliedFilters.length;
    const isMatchingInboxFilter =
      !currentInbox || Number(currentInbox) === inboxId;
    if (
      !hasAppliedFilters &&
      !isOnFoldersView(rootState) &&
      !isOnMentionsView(rootState) &&
      !isOnUnattendedView(rootState) &&
      isMatchingInboxFilter
    ) {
      commit(types.CONTACT_ADD_CONVERSATION, conversation);
      dispatch('contacts/setContact', sender);
    }
  },

  addContactViewMentions({ dispatch, rootState }, conversation) {
    if (isOnMentionsView(rootState)) {
      dispatch('updateContactViewConversation', conversation);
    }
  },

  addContactViewUnattended({ dispatch, rootState }, conversation) {
    if (isOnUnattendedView(rootState)) {
      dispatch('updateContactViewConversation', conversation);
    }
  },

  updateContactViewConversation({ commit, dispatch }, conversation) {
    const {
      meta: { sender },
    } = conversation;
    commit(types.CONTACT_UPDATE_CONVERSATION, conversation);

    dispatch('conversationLabels/setConversationLabel', {
      id: conversation.id,
      data: conversation.labels,
    });

    dispatch('contacts/setContact', sender);
  },

  updateContactViewConversationLastActivity(
    { commit },
    { conversationId, lastActivityAt }
  ) {
    commit(types.CONTACT_UPDATE_CONVERSATION_LAST_ACTIVITY, {
      lastActivityAt,
      conversationId,
    });
  },

  setContactViewChatStatusFilter({ commit }, data) {
    commit(types.CONTACT_CHANGE_CHAT_STATUS_FILTER, data);
  },

  setContactViewChatSortFilter({ commit }, data) {
    commit(types.CONTACT_CHANGE_CHAT_SORT_FILTER, data);
  },

  updateContactViewAssignee({ commit }, data) {
    commit(types.CONTACT_UPDATE_ASSIGNEE, data);
  },

  updateContactViewConversationContact({ commit }, data) {
    if (data.id) {
      commit(`contacts/${types.SET_CONTACT_ITEM}`, data);
    }
    commit(types.CONTACT_UPDATE_CONVERSATION_CONTACT, data);
  },

  updateContactViewSenderLabels(
    { commit, dispatch, state, rootGetters },
    { contactId, labels, activeLabel }
  ) {
    commit(types.CONTACT_UPDATE_SENDER_LABELS, { contactId, labels });

    // If there's an active label filter and the contact no longer has that label,
    // remove the contact from the list and clear the selected state
    const currentLabelFilter = activeLabel || state.conversationFilters?.labels?.[0];
    if (currentLabelFilter && !labels.includes(currentLabelFilter)) {
      const numericContactId = Number(contactId);

      // Find conversations for this contact to update stats before removing
      const contactConversations = state.allContactViewConversations.filter(
        c => c.meta?.sender?.id === numericContactId
      );

      // Update tab counts for each conversation being removed
      const currentUserId = rootGetters.getCurrentUser?.id;
      contactConversations.forEach(conversation => {
        const assigneeId = conversation.meta?.assignee?.id || null;
        const isAssignedToMe = assigneeId === currentUserId;
        const isUnassigned = !assigneeId;

        // Decrease the appropriate counters
        let mineCountDelta = isAssignedToMe ? -1 : 0;
        let unAssignedCountDelta = isUnassigned ? -1 : 0;

        if (mineCountDelta !== 0 || unAssignedCountDelta !== 0) {
          commit(
            `contactViewConversationStats/${types.CONTACT_UPDATE_CONV_TAB_META}`,
            { mineCountDelta, unAssignedCountDelta, allCountDelta: -1 },
            { root: true }
          );
        } else {
          // Only decrease allCount if not mine or unassigned
          commit(
            `contactViewConversationStats/${types.CONTACT_UPDATE_CONV_TAB_META}`,
            { mineCountDelta: 0, unAssignedCountDelta: 0, allCountDelta: -1 },
            { root: true }
          );
        }
      });

      // Check if the removed contact is the currently selected one
      if (state.selectedContactId === numericContactId) {
        dispatch('clearContactViewSelectedState');
      }
      commit(types.CLEAR_CONTACT_CONVERSATIONS, numericContactId);
    }
  },

  setContactViewActiveInbox({ commit }, inboxId) {
    commit(types.CONTACT_SET_ACTIVE_INBOX, inboxId);
  },

  muteContactViewConversation: async ({ commit }, conversationId) => {
    try {
      await ContactConversationApi.mute(conversationId);
      commit(types.CONTACT_MUTE_CONVERSATION);
    } catch (error) {
      //
    }
  },

  unmuteContactViewConversation: async ({ commit }, conversationId) => {
    try {
      await ContactConversationApi.unmute(conversationId);
      commit(types.CONTACT_UNMUTE_CONVERSATION);
    } catch (error) {
      //
    }
  },

  sendContactViewEmailTranscript: async (_, { conversationId, email }) => {
    try {
      await ContactConversationApi.sendEmailTranscript({ conversationId, email });
    } catch (error) {
      throw new Error(error);
    }
  },

  updateContactViewCustomAttributes: async (
    { commit },
    { conversationId, customAttributes }
  ) => {
    try {
      const response = await ContactConversationApi.updateCustomAttributes({
        conversationId,
        customAttributes,
      });
      const { custom_attributes } = response.data;
      commit(types.CONTACT_UPDATE_CONVERSATION_CUSTOM_ATTRIBUTES, {
        conversationId,
        customAttributes: custom_attributes,
      });
    } catch (error) {
      // Handle error
    }
  },

  setContactViewConversationFilters({ commit }, data) {
    commit(types.CONTACT_SET_CONVERSATION_FILTERS, data);
  },

  clearContactViewConversationFilters({ commit }) {
    commit(types.CONTACT_CLEAR_CONVERSATION_FILTERS);
  },

  setContactViewChatListFilters({ commit }, data) {
    commit(types.CONTACT_SET_CHAT_LIST_FILTERS, data);
  },

  updateContactViewChatListFilters({ commit }, data) {
    commit(types.CONTACT_UPDATE_CHAT_LIST_FILTERS, data);
  },

  assignContactViewPriority: async ({ dispatch }, { conversationId, priority }) => {
    try {
      await ContactConversationApi.togglePriority({
        conversationId,
        priority,
      });

      dispatch('setContactViewCurrentChatPriority', {
        priority,
        conversationId,
      });
    } catch (error) {
      // Handle error
    }
  },

  setContactViewCurrentChatPriority({ commit }, { priority, conversationId }) {
    commit(types.CONTACT_ASSIGN_PRIORITY, { priority, conversationId });
  },

  setContactViewContextMenuChatId({ commit }, chatId) {
    commit(types.CONTACT_SET_CONTEXT_MENU_CHAT_ID, chatId);
  },

  ...messageReadActions,
  ...messageTranslateActions,
};

export default actions;
